/**
 * WakeLockManager
 * 
 * A self-contained class that handles:
 * 1. Acquiring and releasing the browser Wake Lock API
 * 2. Showing a fallback modal with OS-specific instructions when wake lock fails
 * 3. Showing a post-session toast reminder to restore display settings
 * 
 * Usage:
 *   const wakeLock = new WakeLockManager({
 *     onReminderPreferenceChange: (shouldRemind) => {
 *       // Wire this to your preference store
 *     }
 *   });
 * 
 *   // When a session or Myra conversation starts:
 *   await wakeLock.acquire();
 * 
 *   // When the session or conversation ends:
 *   wakeLock.release();
 * 
 *   // Clean up when component unmounts:
 *   wakeLock.destroy();
 */

class WakeLockManager {
    constructor(options = {}) {
        // Callback to wire into your preference store
        this.onReminderPreferenceChange = options.onReminderPreferenceChange || null;

        // Internal state
        this._wakeLockSentinel = null;
        this._isActive = false;
        this._remindToRestore = true; // default checked
        this._detectedOS = this._detectOS();
        this._modalElement = null;
        this._toastElement = null;
        this._toastTimeout = null;

        // Re-acquire wake lock when user returns to tab
        this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }


    // ─────────────────────────────────────────────
    // 1. WAKE LOCK — Acquire, release, re-acquire
    // ─────────────────────────────────────────────

    /**
     * Attempt to acquire a wake lock.
     * If it fails, shows the fallback modal with OS-specific instructions.
     * Returns true if wake lock was acquired, false if fallback modal was shown.
     */
    async acquire() {
        console.log('WakeLockManager: Attempting to acquire wake lock...');
        // Already active — nothing to do
        if (this._isActive && this._wakeLockSentinel) {
            return true;
        }

        try {
            if ('wakeLock' in navigator) {
                this._wakeLockSentinel = await navigator.wakeLock.request('screen');
                this._isActive = true;

                // Listen for unexpected release (e.g. tab switch, OS override)
                this._wakeLockSentinel.addEventListener('release', () => {
                    this._isActive = false;
                    this._wakeLockSentinel = null;
                });

                return true;
            } else {
                // Browser does not support Wake Lock API
                this._showFallbackModal();
                return false;
            }
        } catch (err) {
            // Wake Lock request failed (e.g. page not visible, low battery, permission denied)
            console.warn('WakeLockManager: Wake lock request failed —', err.message);
            this._showFallbackModal();
            return false;
        }
    }

    /**
     * Release the wake lock and optionally show the post-session reminder toast.
     */
    release() {
        // Release the wake lock if active
        if (this._wakeLockSentinel) {
            this._wakeLockSentinel.release();
            this._wakeLockSentinel = null;
            this._isActive = false;
        }

        // Show reminder toast if the user opted in
        if (this._remindToRestore) {
            this._showReminderToast();
        }
    }

    /**
     * Returns whether the wake lock is currently active.
     */
    get isActive() {
        return this._isActive;
    }

    /**
     * Re-acquire the wake lock when user returns to the tab.
     * Only attempts if we previously had an active session.
     */
    async _handleVisibilityChange() {
        if (document.visibilityState === 'visible' && !this._isActive && this._wakeLockSentinel === null) {
            // Only re-acquire if we were previously managing a lock
            // (i.e., acquire() was called but lock was released by tab switch)
            this.reacquire()
        }
    }

    /**
     * Re-acquire after tab becomes visible again.
     * Call this if you want to explicitly re-acquire after visibility change.
     */
    async reacquire() {
        if (window.allowLockVisibility) {
            if (!this._isActive) {
                return await this.acquire();
            }
            return true;
        }
        return;
    }


    // ─────────────────────────────────────────────
    // 2. FALLBACK MODAL — OS-specific instructions
    // ─────────────────────────────────────────────

    _showFallbackModal() {
        // Don't show duplicate modals
        if (this._modalElement) return;

        const os = this._detectedOS;
        const instructions = this._getInstructions(os);

        // Create overlay
        const overlay = document.createElement('div');
        overlay.id = 'wakelock-modal-overlay';
        overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(15, 23, 42, 0.5);
      display: flex; align-items: center; justify-content: center;
      z-index: 10000;
      font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

        const isMobile = os === 'ios' || os === 'android';
        const modalMaxWidth = isMobile ? '360px' : '520px';
        const modalPadding = isMobile ? '1.5rem' : '2rem 2.25rem 1.75rem';
        const bodyFontSize = isMobile ? '0.85rem' : '0.95rem';
        const bodyLineHeight = isMobile ? '1.5' : '1.65';
        const stepsFontSize = isMobile ? '0.82rem' : '0.9rem';
        const checkboxFontSize = isMobile ? '0.78rem' : '0.85rem';
        const iconSize = isMobile ? '40px' : '48px';
        const svgSize = isMobile ? '20' : '24';

        const iconSVG = isMobile
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`;

        overlay.innerHTML = `
      <div style="
        background: #fff;
        border-radius: 16px;
        padding: ${modalPadding};
        max-width: ${modalMaxWidth};
        width: 90%;
        box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.06);
        position: relative;
      ">
        <!-- Teal top accent bar -->
        <div style="position:absolute;top:0;left:0;right:0;height:4px;background:#0d9488;border-radius:16px 16px 0 0;"></div>

        <!-- Icon -->
        <div style="
          width:${iconSize};height:${iconSize};
          background:#e6f7f6;border-radius:12px;
          display:flex;align-items:center;justify-content:center;
          margin-bottom:${isMobile ? '1rem' : '1.25rem'};
        ">
          ${iconSVG}
        </div>

        <!-- Heading -->
        <p style="font-size:${isMobile ? '1rem' : '1.1rem'};font-weight:700;color:#111827;line-height:1.3;margin:0 0 ${isMobile ? '0.6rem' : '0.75rem'} 0;">
          Keep your screen on
        </p>

        <!-- Body -->
        <p style="font-size:${bodyFontSize};font-weight:400;color:#475569;line-height:${bodyLineHeight};margin:0 0 ${isMobile ? '1rem' : '1.25rem'} 0;">
          While you're talking with Myra, your screen may dim or lock because you won't be touching your device.
          <br><br>
          To avoid interruptions, please adjust your display settings so the screen stays on.
        </p>

        <!-- Instructions box -->
        <div style="
          background:#f0faf9;border-left:3px solid #0d9488;
          border-radius:0 8px 8px 0;
          padding:${isMobile ? '0.7rem 0.85rem' : '0.875rem 1rem'};
          margin-bottom:${isMobile ? '1rem' : '1.25rem'};
        ">
          <p style="font-size:${stepsFontSize};font-weight:500;color:#1e293b;line-height:1.6;margin:0;">
            <span style="font-weight:600;color:#111827;">${instructions.path}</span><br>
            ${instructions.action}
          </p>
        </div>

        <!-- Checkbox -->
        <label id="wakelock-checkbox-row" style="
          display:flex;align-items:center;gap:0.6rem;
          margin-bottom:${isMobile ? '1.15rem' : '1.5rem'};cursor:pointer;
        ">
          <input type="checkbox" id="wakelock-remind-checkbox" checked style="
            width:18px;height:18px;accent-color:#0d9488;cursor:pointer;
          ">
          <span style="font-size:${checkboxFontSize};font-weight:400;color:#475569;line-height:1.4;">
            Remind me to change this back after my session
          </span>
        </label>

        <!-- Button -->
        <button id="wakelock-dismiss-btn" style="
          display:block;width:100%;padding:14px 28px;
          background:#0d9488;color:#fff;border:none;border-radius:8px;
          font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;
          cursor:pointer;
          box-shadow:0 4px 14px rgba(13,148,136,0.25);
          transition:background 0.2s ease;
        ">
          I'm all set
        </button>
      </div>
    `;

        document.body.appendChild(overlay);
        this._modalElement = overlay;

        // Wire up checkbox
        const checkbox = overlay.querySelector('#wakelock-remind-checkbox');
        checkbox.addEventListener('change', () => {
            this._remindToRestore = checkbox.checked;
            if (this.onReminderPreferenceChange) {
                this.onReminderPreferenceChange(checkbox.checked);
            }
        });

        // Wire up dismiss button
        const btn = overlay.querySelector('#wakelock-dismiss-btn');
        btn.addEventListener('mouseenter', () => {
            btn.style.background = '#0f766e';
        });
        btn.addEventListener('mouseleave', () => {
            btn.style.background = '#0d9488';
        });
        btn.addEventListener('click', () => {
            this._dismissModal();
        });
    }

    _dismissModal() {
        if (this._modalElement) {
            this._modalElement.remove();
            this._modalElement = null;
        }
    }


    // ─────────────────────────────────────────────
    // 3. POST-SESSION TOAST — Restore settings
    // ─────────────────────────────────────────────

    _showReminderToast() {
        // Don't show duplicate toasts
        if (this._toastElement) return;

        const os = this._detectedOS;
        const instructions = this._getInstructions(os);

        const toast = document.createElement('div');
        toast.id = 'wakelock-reminder-toast';
        toast.style.cssText = `
      position: fixed; top: 24px; left: 50%; transform: translateX(-50%);
      background: #fff;
      border-radius: 12px;
      padding: 1rem 1.5rem;
      max-width: 480px;
      width: 90%;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.06);
      border-left: 3px solid #0d9488;
      z-index: 10001;
      font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      animation: wakelock-toast-in 0.3s ease-out;
    `;

        toast.innerHTML = `
      <svg style="width:20px;height:20px;flex-shrink:0;color:#0d9488;margin-top:2px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span style="font-size:0.88rem;font-weight:400;color:#475569;line-height:1.55;">
        Don't forget to restore your display settings.
        <span style="font-weight:600;color:#111827;">${instructions.path}</span>
        — set it back to your preferred time.
      </span>
    `;

        // Add the animation keyframes if not already present
        if (!document.getElementById('wakelock-toast-styles')) {
            const style = document.createElement('style');
            style.id = 'wakelock-toast-styles';
            style.textContent = `
        @keyframes wakelock-toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes wakelock-toast-out {
          from { opacity: 1; transform: translateX(-50%) translateY(0); }
          to { opacity: 0; transform: translateX(-50%) translateY(-12px); }
        }
      `;
            document.head.appendChild(style);
        }

        document.body.appendChild(toast);
        this._toastElement = toast;

        // Auto-dismiss after 3 seconds
        this._toastTimeout = setTimeout(() => {
            this._dismissToast();
        }, 3000);
    }

    _dismissToast() {
        if (this._toastElement) {
            this._toastElement.style.animation = 'wakelock-toast-out 0.3s ease-in forwards';
            setTimeout(() => {
                if (this._toastElement) {
                    this._toastElement.remove();
                    this._toastElement = null;
                }
            }, 300);
        }
        if (this._toastTimeout) {
            clearTimeout(this._toastTimeout);
            this._toastTimeout = null;
        }
    }


    // ─────────────────────────────────────────────
    // OS DETECTION & INSTRUCTIONS
    // ─────────────────────────────────────────────

    _detectOS() {
        const ua = navigator.userAgent || '';
        const platform = navigator.platform || '';

        // iOS detection (iPhone, iPad, iPod)
        if (/iPhone|iPad|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
            return 'ios';
        }

        // Android
        if (/Android/.test(ua)) {
            return 'android';
        }

        // macOS (non-touch, so not iPad pretending to be Mac)
        if (/Macintosh|Mac OS X/.test(ua)) {
            return 'macos';
        }

        // Windows
        if (/Windows/.test(ua)) {
            return 'windows';
        }

        // Fallback — default to generic desktop instructions
        return 'unknown';
    }

    _getInstructions(os) {
        const instructionMap = {
            macos: {
                path: 'System Settings → Lock Screen',
                action: 'Set "Turn display off" to 30 minutes or Never.'
            },
            windows: {
                path: 'Settings → System → Power & sleep',
                action: 'Under "Screen," set it to 30 minutes or Never.'
            },
            ios: {
                path: 'Settings → Display & Brightness → Auto-Lock',
                action: 'Set to Never.'
            },
            android: {
                path: 'Settings → Display → Screen timeout',
                action: 'Set to the longest option available.'
            },
            unknown: {
                path: 'Your device display settings',
                action: 'Set your screen timeout to the longest option or "Never."'
            }
        };

        return instructionMap[os] || instructionMap.unknown;
    }


    // ─────────────────────────────────────────────
    // CLEANUP
    // ─────────────────────────────────────────────

    /**
     * Clean up all resources. Call this when the component unmounts
     * or the page is being torn down.
     */
    destroy() {
        this.release();
        this._dismissModal();
        this._dismissToast();
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    }
}

export default WakeLockManager;